import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Typography, Switch, TextField, FormControlLabel, FormControl, InputLabel, Select, MenuItem, FormHelperText, OutlinedInput, InputAdornment, IconButton } from '@mui/material';
import dayjs from '../../../../config/dayjs';

import { getAll as getAllDrives } from '../../../../services/driveService';
import { getAll as getAllCourses } from '../../../../services/courseService';
import { getAll as getAllBatches } from '../../../../services/batchService';
import { createUser, getById, updateById } from '../../../../services/usersService';
import { getExamDates, getAll } from '../../../../services/masterService';
import { useToaster } from '../../../../providers/ToasterProvider';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import RadiusButton from '../../../../components/RadiusButton';
import { UserCreationMail } from '../UserCreationMail/UserCreationMail';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import EYE_ICON from '../../../../assets/images/eye.svg';
import EYE_CLOSE_ICON from '../../../../assets/images/eye-close.svg'
import BACK from '../../../../assets/images/back.svg';

import { LoadingButton } from '@mui/lab';
import { ProjectContext } from '../../../../providers/projectProvider';
import patterns from '../../../../constant/patterns';

export const ObjectionTrackerDetails = () => {

    const params = useParams();
    const projectName = params?.projectName;
    const location = useLocation();
    const navigate = useNavigate();
    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);

    const [isActive, setIsActive] = useState(true);
    const [driveId, setDriveId] = useState(0);
    const [drives, setDrives] = useState([]);
    const [courseId, setCourseId] = useState(0);
    const [allCourses, setAllCourses] = useState([]);
    const [courses, setCourses] = useState([]);
    const [batchId, setBatchId] = useState(0);
    const [allBatches, setAllBatches] = useState([]);
    const [batches, setBatches] = useState([]);
    const [allProjectMasters, setAllProjectMasters] = useState([]);
    const [examDate, setExamDate] = useState('');
    const [examDates, setExamDates] = useState([]);
    const [userDetails, setUserDetails] = useState(null);
    const [mailModal, setMailModal] = useState({
        isOpen: false,
        isUpdated: false,
        cancelPressed: false,
    });
    const [editMode, setEditMode] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const itSupportSchema = z.object({
        firstName: z.string().min(1, { message: 'First name is required.' }),
        lastName: z.string().min(1, { message: 'Last name is required.' }),
        username: z.string().min(1, { message: 'User name is required.' }),
        email: z.string().min(1, { message: 'User id is required.' }).email('Please enter valid email address.'),
        password: z.string(),
        drive: z.number().gte(1),
        course: z.number().gte(1),
        batch: z.number().gte(1),
        examDate: z.string().min(1, { message: 'Please select exam date.' }),
    }).superRefine(({ username, password }, ctx) => {
        if ((editMode && username && (username.length < 8 || username.length > 20)) || (!editMode && (username.length < 8 || username.length > 20))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Username should be at least 8 characters long and shorter than 20 characters',
                path: ['username']
            });
        }
        if ((editMode && username && !patterns.USERNAME.test(username)) || (!editMode && !patterns.USERNAME.test(username))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Username must contain only alphanumeric characters, underscores, periods, or hyphens',
                path: ['username']
            });
        }
        if ((editMode && password && password.length < 8) || (!editMode && password.length < 8)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Password should be at least 8 characters long',
                path: ['password']
            });
        }
        if ((editMode && password && !patterns.PASSWORD.test(password)) || (!editMode && !patterns.PASSWORD.test(password))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Password must contain one uppercase character, one lowercase character, one digit and one special character',
                path: ['password']
            });
        }
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        setValue,
    } = useForm({
        resolver: zodResolver(itSupportSchema),
        // defaultValues: {
        //     firstName: 'itsupport',
        //     lastName: 'user',
        //     email: 'itsupport@test.com',
        //     password: 'test@123'
        // }
    });

    useEffect(() => {
        const fetchRequiredData = async () => {
            try {
                const driveRes = await getAllDrives();
                if (driveRes.code === 'OK') {
                    setDrives([...driveRes.data]);
                }
                const courseRes = await getAllCourses();
                if (courseRes.code === 'OK') {
                    setAllCourses([...courseRes.data]);
                }
                const batchRes = await getAllBatches();
                if (batchRes.code === 'OK') {
                    setAllBatches([...batchRes.data]);
                }
                const pmRes = await getAll();
                if (pmRes.code === 'OK') {
                    setAllProjectMasters([...pmRes.data.list]);
                }


                const userId = location?.state?.userId;
                if (userId) {
                    const fetchUserDetails = async () => {
                        try {
                            const res = await getById(userId);
                            if (res.code === 'OK') {
                                const details = res.data;
                                setEditMode(true);
                                setValue('firstName', details.firstName);
                                setValue('lastName', details.lastName);
                                setValue('username', details.username);
                                setValue('email', details.email);
                                setIsActive(details.isActive);
                                setValue('drive', details.driveId);
                                setDriveId(details.driveId);
                                setTimeout(() => {
                                    setValue('course', details.courseId);
                                    setCourseId(details.courseId);
                                }, 100);
                                setTimeout(() => {
                                    setValue('batch', details.batchId);
                                    setBatchId(details.batchId);
                                }, 200);
                                setTimeout(() => {
                                    const formatedDate = `${details.examDate.substring(8, 10)}-${details.examDate.substring(5, 7)}-${details.examDate.substring(0, 4)}`
                                    setValue('examDate', formatedDate);
                                    setExamDate(formatedDate);
                                }, 300);
                                setUserDetails(details);
                            }
                        } catch (error) {
                            console.log('There is an error while getting it support user details', error);
                        }
                    }
                    fetchUserDetails();
                }

            } catch (error) {
                console.log('There is an error while fetching required data', error);
            }
        }
        fetchRequiredData();
    }, [location]);

    useEffect(() => {
        if (mailModal.cancelPressed) {
            const path = location.pathname.replace('/objection-tracker-details', '');
            navigate(path);
        }
    }, [mailModal, navigate, location]);

    useEffect(() => {
        const filteredCourses = [];
        if (driveId !== 0) {
            for (let pm of allProjectMasters) {
                if (pm.driveId === driveId) {
                    const course = allCourses.find((course) => course.id === pm.courseId);
                    if (course && !filteredCourses.some((c) => c.id === course.id)) {
                        filteredCourses.push(course);
                    }
                }
            }
        }
        setCourses([...filteredCourses]);
        setValue('course', 0);
        setCourseId(0);
        setBatches([]);
        setValue('batch', 0);
        setBatchId(0);
        setValue('examDate', '');
        setExamDate('')
    }, [driveId]);

    useEffect(() => {
        const filteredBatches = [];
        if (courseId !== 0) {
            for (let pm of allProjectMasters) {
                if (pm.driveId === driveId && pm.courseId === courseId) {
                    const batch = allBatches.find((batch) => batch.id === pm.batchId);
                    if (batch && !filteredBatches.some((c) => c.id === batch.id)) {
                        filteredBatches.push(batch);
                    }
                }
            }
        }
        setBatches([...filteredBatches]);
        setValue('batch', 0);
        setBatchId(0);
        setValue('examDate', '');
        setExamDate('')
    }, [courseId]);

    useEffect(() => {
        if (driveId === 0 || courseId === 0 || batchId === 0) {
            setValue('examDate', '');
            setExamDate('')
        }
        if (driveId && courseId && batchId) {
            const fetchExamDates = async () => {
                try {
                    const body = { driveId, courseId, batchId };
                    const res = await getExamDates(body);
                    if (res.code === 'OK') {
                        const projectMasters = res.data?.projectMasters;
                        if (!projectMasters.length) {
                            addNotification('Exam date not found for selected drive, course and batch.', 'warning');
                        }
                        setExamDates(() => {
                            return projectMasters.map((pm) => {
                                const date = dayjs(pm.examDate);
                                return date.format('DD-MM-YYYY')
                            });
                        });
                    }
                } catch (error) {
                    console.log('There is an error while fetching exam dates', error);
                }
            }
            fetchExamDates();
        }

    }, [driveId, courseId, batchId, examDate])

    const onGoBack = () => {
        if (projectName) {
            navigate(`/${projectName}/objection-tracker`);
        }
    }
    const submitForm = async (values) => {
        if (!values.drive || !values.course || !values.batch || !values.examDate) {
            return;
        }
        const date = `${examDate.substring(3, 5)}-${examDate.substring(0, 2)}-${examDate.substring(6, 10)}`
        const body = {
            firstName: values.firstName,
            lastName: values.lastName,
            username: values.username,
            email: values.email,
            password: values.password,
            driveId: driveId,
            courseId: courseId,
            batchId: batchId,
            examDate: date,
            isActive: isActive
        }

        if (editMode) {
            setIsLoading(true);
            try {
                if (!body.password) {
                    delete body.password;
                }
                const res = await updateById(userDetails.id, body);
                if (res.code === 'OK') {
                    addNotification('User details updated successfully', 'success');
                    if (body.password) {
                        setUserDetails({
                            ...res.data.user,
                            password: values.password,
                        });
                        setMailModal({
                            ...mailModal,
                            isUpdated: true,
                            isOpen: true
                        });
                    } else {
                        onGoBack();
                    }
                }
            } catch (error) {
                console.log('There is an error while updating it support user', error);
            } finally {
                setIsLoading(false);
            }
        } else {
            setIsLoading(true);
            try {
                body.userType = 9;
                const res = await createUser(body);
                if (res.code === 'CREATED') {
                    const data = {
                        ...res.data.user,
                        password: values.password
                    }
                    setUserDetails(data);
                    setMailModal({
                        ...mailModal,
                        isOpen: true,
                    });
                    addNotification('User added successfully', 'success');
                }
            } catch (error) {
                if (error?.response?.data?.data?.code === 'E_DUPLICATE') {
                    addNotification('User name is taken.', 'danger');
                }
                console.log('There is an error while adding it support user', error);
            } finally {
                setIsLoading(false);
            }
        }

    }
    const handleActiveChange = () => {
        setIsActive(!isActive)
    }

    return (
        <MainContainer sx={{ bgcolor: 'red' }}>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{
                        fontWeight: 400,
                        textTransform: 'capitalize'
                    }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                        Objection Tracker
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>

                <div className="search-header-wrapper" style={{ marginBottom: '24px' }}>
                    <Typography component='h5' variant='h5' sx={{
                        mr: 'auto',
                    }}>
                        {editMode ? 'Edit' : 'Create'} Objection Tracker User
                    </Typography>
                </div>

                <Box sx={{
                    bgcolor: 'background.base',
                    borderRadius: 6,
                    p: 4
                }} className='create-user-form-wrapper'>
                    <Box component='form' autoComplete='off' onSubmit={handleSubmit(submitForm)}>
                        <div className="user-form-details">

                            <TextField
                                id='firstName'
                                name='firstName'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>First Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('firstName')}
                                error={Boolean(errors.firstName)}
                                helperText={errors.firstName?.message}
                                onBlur={() => trigger('firstName')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                            />

                            <TextField
                                id='lastName'
                                name='lastName'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>Last Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('lastName')}
                                error={Boolean(errors.lastName)}
                                helperText={errors.lastName?.message}
                                onBlur={() => trigger('lastName')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                            />

                            <TextField
                                id='username'
                                name='username'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>User Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('username')}
                                error={Boolean(errors.username)}
                                helperText={errors.username?.message}
                                onBlur={() => trigger('username')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                                disabled={editMode ? true : false}
                            />

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <FormControl variant='outlined' sx={{ mb: 2 }}>
                                    <InputLabel htmlFor="passwordLabel">
                                        <Box component='span'>Password</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </InputLabel>
                                    <OutlinedInput
                                        id='passwordLabel'
                                        type={passwordVisible ? 'text' : 'password'}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setPasswordVisible(!passwordVisible)}
                                                    edge="end"
                                                >
                                                    <img className='password-eye' src={passwordVisible ? EYE_CLOSE_ICON : EYE_ICON} alt='' />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        label="Password"
                                        name="password"
                                        {...register('password')}
                                        error={Boolean(errors.password)}
                                        onBlur={() => trigger('password')}
                                    />
                                    {errors?.password?.message && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.password?.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                {editMode && <FormHelperText sx={{ mt: '-10px', color: 'primary.main', mb: 1.5 }}>
                                    Enter password only if you wish to update.
                                </FormHelperText>}
                            </Box>

                            <TextField
                                id='email'
                                name='email'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>Email</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('email')}
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                                onBlur={() => trigger('email')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                                disabled={editMode ? true : false}
                            />

                            <FormControl className='form-field' sx={{
                                maxWidth: '100%',
                                flexGrow: 1
                            }}>
                                <InputLabel id="driveNameLabel">
                                    Drive Name
                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                </InputLabel>
                                <Select
                                    labelId="driveNameLabel"
                                    id="drive"
                                    label='Exam Hall Name*'
                                    margin='none'
                                    name='drive'
                                    error={Boolean(errors.drive)}
                                    onBlur={() => trigger('drive')}
                                    value={driveId}
                                    onChange={(e, event) => {
                                        setDriveId(event.props.value);
                                        setValue('drive', event.props.value);
                                    }}
                                >
                                    <MenuItem value={0}>None</MenuItem>
                                    {
                                        drives.map(
                                            (drive) => <MenuItem key={drive.id} value={drive.id}>
                                                {drive.driveName}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {errors.drive?.message && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        Please select drive name
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl className='form-field' sx={{
                                maxWidth: '100%',
                                flexGrow: 1
                            }}>
                                <InputLabel id="courseNameLabel">
                                    Course Name
                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                </InputLabel>
                                <Select
                                    labelId="courseNameLabel"
                                    id="course"
                                    label='Course Name*'
                                    margin='none'
                                    name='course'
                                    error={Boolean(errors.course)}
                                    onBlur={() => trigger('course')}
                                    value={courseId}
                                    onChange={(e, event) => {
                                        setCourseId(event.props.value);
                                        setValue('course', event.props.value);
                                    }}
                                >
                                    <MenuItem value={0}>None</MenuItem>
                                    {
                                        courses.map(
                                            (course) => <MenuItem key={course.id} value={course.id}>
                                                {course.courseName}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {errors.course?.message && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        Please select course name
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl className='form-field' sx={{
                                maxWidth: '100%',
                                flexGrow: 1
                            }}>
                                <InputLabel id="batchNameLabel">Batch Name*</InputLabel>
                                <Select
                                    labelId="batchNameLabel"
                                    id="batch"
                                    label='Batch Name*'
                                    margin='none'
                                    name='batch'
                                    error={Boolean(errors.batch)}
                                    onBlur={() => trigger('batch')}
                                    value={batchId}
                                    onChange={(e, event) => {
                                        setBatchId(event.props.value);
                                        setValue('batch', event.props.value);
                                    }}
                                >
                                    <MenuItem value={0}>None</MenuItem>
                                    {
                                        batches.map(
                                            (batch) => <MenuItem key={batch.id} value={batch.id}>
                                                {batch.batchName}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {errors.batch?.message && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        Please select batch name
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl className='form-field' sx={{
                                maxWidth: '100%',
                                flexGrow: 1
                            }}>
                                <InputLabel id="examDateLabel">Exam Date*</InputLabel>
                                <Select
                                    labelId="examDateLabel"
                                    id="examDate"
                                    label='Exam Date*'
                                    margin='none'
                                    name='examDate'
                                    error={Boolean(errors.examDate)}
                                    onBlur={() => trigger('examDate')}
                                    value={examDate}
                                    onChange={(e, event) => {
                                        setExamDate(event.props.value);
                                        setValue('examDate', event.props.value);
                                    }}
                                >
                                    <MenuItem value=''>None</MenuItem>
                                    {
                                        examDates.map(
                                            (examDate) => <MenuItem key={examDate} value={examDate}>
                                                {examDate}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {errors.examDate?.message && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        Please select exam date
                                    </FormHelperText>
                                )}
                            </FormControl>

                        </div>

                        <FormControlLabel sx={{
                            mb: 3
                        }} control={
                            <Switch
                                checked={isActive}
                                onChange={handleActiveChange}
                            />
                        } label={<Box component='span'>Active</Box>} />

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <RadiusButton sx={{
                                textTransform: 'uppercase',
                                fontWeight: 500,
                            }} color='grey'
                                variant='contained'
                                size='large'
                                onClick={onGoBack}
                                startIcon={<img src={BACK} alt='' />}>
                                Back
                            </RadiusButton>
                            <LoadingButton sx={{
                                textTransform: 'uppercase',
                                borderRadius: 40
                            }} color='primary'
                                type="submit"
                                variant='contained'
                                size='large'
                                loading={isLoading}
                            >
                                Submit
                            </LoadingButton>
                        </Box>

                    </Box>
                </Box>

            </Container>
            <UserCreationMail userDetails={userDetails} openState={mailModal.isOpen} isUpdated={mailModal.isUpdated} setMailModal={setMailModal} />
        </MainContainer >
    );
}
